import { FC, useEffect, useState, useCallback } from 'react';
import Swal from 'sweetalert2';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';

import { Grid, FormControl, MenuItem, Select, InputLabel, IconButton, Menu, Box } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { SCUIButton, SCUINoRecordComponent, SCUILoadingLinear } from '../../atomic/atoms';
import { SCUIContent } from '../../atomic/organisms';
import { useTranslation } from 'react-i18next';
import { SCUIBreadCrumb, SCUICustomDataGrid, SCUIGeneralDrawer } from '../../atomic/molecules';
import { CargoManagementOrderLimitDrawerContent } from './components';

import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { CargoManagementQuery } from '../../services/query';
import CargoManagementCommand from '../../services/command/cargoManagement/CargoManagementCommand.services';
import TimeHelper from '../../utils/time';
import SCUIDownloadButton from '../../atomic/atoms/SCUIDownloadButton/SCUIDownloadButton';
import { CMV, cmvCls } from './cargoManagementV2.classes';

export const CargoManagementV2: FC<any> = () => {
    const [companyList, setCompanyList] = useState<any>([]);
    const [gridData, setGridData] = useState<any>();
    const [companyId, setCompanyId] = useState(0);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [loading, setLoading] = useState(false);
    const [drawerEditOpen, setDrawerEditOpen] = useState(false);
    const [selectedOrderLimit, setSelectedOrderLimit] = useState<any>();
    const [excelData, setExcelData] = useState<any>();

    const { t } = useTranslation();

    const GetCargoCompanyList = useCallback(() => {
        try {
            setLoading(true);
            CargoManagementQuery.CargoCompanyList().then((res: any) => {
                if (res.data) {
                    setCompanyList([...[{ id: '0', name: t('general.all') }], ...res.data]);
                } else {
                    Swal.fire('Service Error', res.errorMessage, 'error');
                }
                setLoading(false);
            });
        } catch (error: any) {
            setLoading(false);
            Swal.fire('Error', error.toString(), 'error');
        } finally {
            setLoading(false);
        }
    }, [t]);

    const columns: ExtendedGridColDef[] = [
        { field: 'id', headerName: 'id', flex: 1, hide: true },
        { field: 'shippingCompanyId', headerName: 'shippingCompanyId', flex: 1, hide: true },
        {
            field: 'shippingCompanyName',
            headerName: t('cargoManagement.shippingCompanyName'),
            flex: 1,
        },
        { field: 'cityName', headerName: t('cargoManagement.cityName'), flex: 1 },
        {
            field: 'countyName',
            headerName: t('cargoManagement.countyName'),
            flex: 1,
        },
        {
            field: 'orderLimit',
            headerName: t('cargoManagement.orderLimit'),
            flex: 1,
        },
        {
            field: 'changeOn',
            headerName: t('cargoManagement.updatedDate'),
            flex: 1,
            renderCell: (params) => {
                return <div style={{ fontSize: '16px' }}>{TimeHelper.formatDateHour(params.row.changeOn)}</div>;
            },
        },
        {
            field: 'userName',
            headerName: t('cargoManagement.updatedBy'),
            flex: 1,
        },
        {
            field: 'deneme',
            headerName: '',
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                const handleClick = (popupState: any, tur: string) => {
                    popupState.close();
                    switch (tur) {
                        case 'gotoDetail': {
                            handleOpenDrawer(true, params);
                            break;
                        }
                        case 'delete': {
                            handleDeleteOrderLimit(params.row);
                            break;
                        }
                        default: {
                            break;
                        }
                    }
                };
                return (
                    <PopupState variant="popover" popupId="demo-popup-menu">
                        {(popupState: any) => (
                            <>
                                <IconButton
                                    {...bindTrigger(popupState)}
                                    className={cmvCls(CMV.input, CMV.inputIconButton, CMV.rowActionsTrigger)}
                                >
                                    <MoreVertIcon className="svg_icons" />
                                </IconButton>
                                <Menu {...bindMenu(popupState)}>
                                    <MenuItem
                                        className={cmvCls(CMV.input, CMV.inputMenuItem, CMV.rowActionEdit)}
                                        onClick={() => handleClick(popupState, 'gotoDetail')}
                                    >
                                        {t('general.edit')}
                                    </MenuItem>
                                    <MenuItem
                                        className={cmvCls(CMV.input, CMV.inputMenuItem, CMV.rowActionDelete)}
                                        onClick={() => handleClick(popupState, 'delete')}
                                    >
                                        {t('general.delete')}
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </PopupState>
                );
            },
        },
    ];

    const CargoSelectedList = async () => {
        setLoading(true);
        try {
            let params = {
                ShippingCompanyId: companyId,
            };
            CargoManagementQuery.CargoCompanyPriorityCity(params).then((res: any) => {
                if (res) {
                    let parseData = (res.data || []).map((item: any) => {
                        return {
                            id: item.id,
                            shippingCompanyId: item.shippingCompanyId,
                            addressEntityId: item.addressEntityId,
                            orderLimit: item.orderLimit,
                            senderLocation: item.senderLocation,
                            isActive: item.isActive,
                            createOn: item.createOn,
                            changeOn: item.changeOn,
                            isDeleted: item.isDeleted,
                            shippingCompanyName: item.shippingCompanyName,
                            userName: item.userName,
                            cityName: item.cityName,
                            countyName: item.countyName,
                        };
                    });
                    setGridData(parseData);
                } else {
                    Swal.fire('Service Error', res.errorMessage, 'error');
                }
                setLoading(false);
            });
        } catch (error: any) {
            setLoading(false);
            Swal.fire('Error', error.toString(), 'error');
        }
    };

    const handleOpenDrawer = (isEdit: boolean, params: any) => {
        if (isEdit && params.row) {
            setIsEdit(true);
            setSelectedOrderLimit(params.row);
        } else {
            setIsEdit(false);
        }

        setDrawerEditOpen(true);
    };

    const onDrawerClose = () => {
        setSelectedOrderLimit(null);
        setIsEdit(false);
        setDrawerEditOpen(false);
    };

    const handleDeleteOrderLimit = (deleteItem: any) => {
        Swal.fire({
            text: t('general.deleteConfirmationText'),
            cancelButtonText: t('general.cancel'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('general.delete'),
        }).then((result) => {
            if (result.isConfirmed) {
                try {
                    setLoading(true);
                    const params = {
                        shippingCompanyId: deleteItem.shippingCompanyId,
                        addressEntityId: deleteItem.addressEntityId,
                        senderLocation: 'string',
                    };
                    CargoManagementCommand.DeleteCargoCompanyPriorityCity(params).then(() => {
                        Swal.fire(t('general.deleted'), ' ', 'success');
                        refresh();
                    });
                } catch (error: any) {
                    setLoading(false);
                    Swal.fire('Error', error.toString(), 'error');
                } finally {
                    setLoading(false);
                }
            }
        });
    };
    const buildExcelData = (data: any) => {
        if (data?.length === 0) return [];
        return data.map((item: any) => {
            return {
                shippingCompanyName: item.shippingCompanyName,
                cityName: item.cityName,
                countyName: item.countyName,
                orderLimit: item.orderLimit,
                changeOn: TimeHelper.formatDateHour(item.changeOn),
                userName: item.userName,
            };
        });
    };

    const refresh = () => {
        CargoSelectedList();
    };

    useEffect(() => {
        GetCargoCompanyList();
    }, [GetCargoCompanyList]);

    useEffect(() => {
        if (gridData) setExcelData(buildExcelData(gridData));
    }, [gridData]);

    return (
        <>
            <SCUIGeneralDrawer
                open={drawerEditOpen}
                width={'400px'}
                onClose={() => {
                    setDrawerEditOpen(false);
                }}
            >
                <CargoManagementOrderLimitDrawerContent
                    selectedOrderLimit={selectedOrderLimit}
                    isEdit={isEdit}
                    defaultSelectedCompany={companyId}
                    companies={companyList}
                    onClose={onDrawerClose}
                    refresh={refresh}
                    setLoading={setLoading}
                />
            </SCUIGeneralDrawer>
            <SCUIContent className={`${CMV.page} `}>
                <>
                    <Grid
                        className={CMV.layout}
                        size={{
                            sm: 12,
                            md: 12,
                            lg: 12,
                            xl: 12,
                        }}
                    >
                        <Grid
                            className={CMV.breadcrumbRow}
                            container
                            direction="row"
                            spacing={2}
                            sx={{ marginLeft: '0.5rem' }}
                        >
                            <SCUIBreadCrumb
                                title={'Kargo Kota Tanımlama Ekranı'}
                                componentLink={'/cargoManagementV2'}
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    setGridData([]);
                                }}
                            />
                            {loading && (
                                <Grid
                                    size={{
                                        xl: 12,
                                        md: 12,
                                        lg: 12,
                                        xs: 12,
                                    }}
                                >
                                    <SCUILoadingLinear display={loading} />
                                </Grid>
                            )}
                        </Grid>
                        <Grid
                            size={{
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 12,
                            }}
                        >
                            {gridData && gridData.length > 0 ? (
                                <Grid
                                    className={CMV.datagridSection}
                                    container
                                    size={{
                                        lg: 12,
                                        md: 12,
                                        xs: 12,
                                    }}
                                >
                                    <Grid
                                        className={CMV.toolbar}
                                        style={{ direction: 'rtl', marginBottom: '16px' }}
                                        size={{
                                            lg: 12,
                                            md: 12,
                                            xs: 12,
                                        }}
                                    >
                                        <SCUIButton
                                            text={t('general.addNew')}
                                            type="primary"
                                            sizeType="fixed"
                                            size={'m'}
                                            style={{ height: '32px' }}
                                            className={cmvCls(CMV.input, CMV.inputButton, CMV.btnAddNew)}
                                            onClick={() => {
                                                handleOpenDrawer(false, null);
                                            }}
                                        />
                                    </Grid>
                                    <Grid
                                        size={{
                                            sm: 12,
                                            md: 12,
                                            lg: 12,
                                            xl: 12,
                                        }}
                                    >
                                        <Box className={CMV.excelDownloadWrap}>
                                            <SCUIDownloadButton
                                                position="right"
                                                excelData={{
                                                    excelDownloadData: excelData,
                                                    hiddenExcelColumn: ['id'],
                                                    excelDownloadFolderName: t('excel.cargoOrderDefiniton'),
                                                }}
                                            />
                                        </Box>
                                        <Box className={CMV.datagrid}>
                                            <SCUICustomDataGrid
                                                rows={gridData}
                                                columns={columns}
                                                sx={{
                                                    '& .MuiDataGrid-cellContent': {
                                                        fontSize: '16px',
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>
                            ) : (
                                <div
                                    className={CMV.filterPanel}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignContent: 'center',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                >
                                    <SCUINoRecordComponent />
                                    <FormControl
                                        className={CMV.fieldFilterCompany}
                                        style={{ width: 450, margin: '20px' }}
                                    >
                                        <InputLabel id="labelApp">{t('cargoManagement.selectCargoCompany')}</InputLabel>
                                        <Select
                                            className={cmvCls(CMV.input, CMV.inputSelect, CMV.fieldFilterCompany)}
                                            label={t('cargoManagement.selectCargoCompany')}
                                            labelId="labelApp"
                                            id="parentMenuLabel"
                                            value={companyId}
                                            onChange={(event) => {
                                                setCompanyId(Number(event.target.value));
                                            }}
                                            fullWidth
                                        >
                                            {companyList &&
                                                companyList.map((comp: any) => (
                                                    <MenuItem key={comp.id} id={comp.id} value={comp.id}>
                                                        {comp.name}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl style={{ width: 450, margin: '10px' }}>
                                        <SCUIButton
                                            text={t('general.filter')}
                                            type="primary"
                                            sizeType="default"
                                            size={'l'}
                                            className={cmvCls(CMV.input, CMV.inputButton, CMV.btnFilter)}
                                            onClick={() => {
                                                CargoSelectedList();
                                            }}
                                        />
                                    </FormControl>
                                </div>
                            )}
                        </Grid>
                    </Grid>
                </>
            </SCUIContent>
        </>
    );
};
